import { runCode, setEngine, setOptions, clearVariables, getVariables } from 'client-side-python-runner';

let pythonInt = setEngine('skulpt');

const originalPrompt = window.prompt;


/**
 * Runs Python code with flexible input handling
 * @param {string} code - The Python code to execute
 * @param {Function|Array} inputHandler - Either:
 *   - Function: async callback(promptText) => string (for interactive input)
 *   - Array: list of input values to provide in order
 * @param {Function} onOutputUpdate - Optional callback for real-time output updates
 * @returns {Promise<{success: boolean, logs?: Array, vars?: Object, error?: Object|string}>}
 */
export async function runPythonCode(code, inputHandler, onOutputUpdate) {
    if (!code) return { success: false, error: "No Code Provided" }

    let error = false
    let output = []
    let inputIndex = 0;
    let inputRequestCount = 0;

    // Determine if inputHandler is array or function
    const isArrayMode = Array.isArray(inputHandler);
    const inputArray = isArrayMode ? inputHandler : [];
    const inputCallback = isArrayMode ? null : inputHandler;

    // Override window.prompt for Python input() calls
    const previousPrompt = window.prompt;
    window.prompt = async (promptText, ...other) => {
        inputRequestCount++;

        // The library doesn't pass the prompt text correctly, use the last output line
        const actualPrompt = output.length > 0 ? output[output.length - 1] : "";
        console.log("Python input requested, using last output as prompt:", actualPrompt); if (isArrayMode) {
            // Array mode: provide inputs from array
            if (inputIndex >= inputArray.length) {
                // User's code asks for too many inputs
                error = `This question asks for ${inputArray.length} input(s), but your code asks for ${inputRequestCount}`;
                throw new Error(error);
            }
            const value = String(inputArray[inputIndex]);
            inputIndex++;
            return value;
        } else if (inputCallback) {
            // Callback mode: call the provided function
            return await inputCallback(actualPrompt);
        } else {
            // Fallback to native prompt
            console.log("No input handler provided.");
            let userInput = originalPrompt(actualPrompt || "");
            if (userInput === null) {
                userInput = "";
            }
            return userInput;
        }
    }

    try {
        setOptions({
            output: (o) => {
                const cleanOutput = o.replace(/\n$/, '');
                output.push(cleanOutput);
                console.log(" " + o);
                // Update logs in real-time if callback provided
                if (onOutputUpdate) {
                    onOutputUpdate(cleanOutput);
                }
            },
            error: (e) => { error = e; console.warn("Python code execution error:", e, e.error) },
            pythonVersion: 3,
            loadVariablesBeforeRun: true,
            storeVariablesAfterRun: true,
            onLoading: () => { console.log("_loading") },
            onLoaded: () => { console.log("_loaded") },
        });
        await clearVariables()
        console.warn("--- Python Execution Start ---")
        try {
            const blocked = ["print", "input", "len", "range", "int", "str", "float", "list", "dict", "set", "tuple", "bool", "type", "isinstance", "id", "help", "dir", "vars", "globals", "locals", "eval", "exec"];
            const pattern = new RegExp(`(^|\\s)(${blocked.join("|")})\\s*=`, "gm");

            code = code.replace(pattern, (match, prefix, funcName) => {
                error = `You cannot use \"${funcName}\" as a new variable, it is a built-in keyword.`;
                return "# [blocked builtin reassignment]";
            });

            await runCode(code);
        } catch (e) {
            console.error("Python Execution(not code) error", e)
            // If error was already set (e.g., from input handler), keep it
            // Otherwise this is a different execution error
            if (!error) {
                error = e;
            }
        }
        console.warn("--- Python Execution End ---")
        let vars = await getVariables()
        console.log("Final Variables:", vars);
        await clearVariables();

        // Check if user's code didn't use all provided inputs
        if (isArrayMode && inputIndex < inputArray.length && !error) {
            error = `This question asks for ${inputArray.length} input(s), but your code only asks for ${inputRequestCount}`;
        }

        if (!error) {
            return { success: true, logs: output, vars: vars }
        } else {
            return { success: false, error: error }
        }
    } finally {
        // Always restore original prompt, even if execution throws an error
        window.prompt = previousPrompt;
    }
}
